import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  History, RefreshCw, Loader2, Undo2, Clock, User, FileSpreadsheet,
  Trash2, Edit3, PlusCircle, RotateCcw, Info,
} from "lucide-react";

/**
 * BookingHistoryTab (iter213 — 02/2026)
 * ------------------------------------------------------------------------
 * Cronologia di TUTTE le modifiche a prenotazioni + blocchi iCal degli
 * ultimi 30 giorni. L'admin può ripristinare qualsiasi evento cliccando
 * "Ripristina". Ogni restore genera a sua volta uno snapshot per poter
 * annullare il ripristino.
 *
 * Eventi tracciati automaticamente:
 *   • Modifiche manuali (cambio stato, extras, totale, cancellazione)
 *   • Import CSV Booking (intero batch = 1 evento)
 *   • Annullamento import CSV
 *   • (in futuro) sync iCal, fusione n?, gift ticket, ecc.
 */
const EVENT_ICONS = {
  manual_create: PlusCircle,
  manual_edit: Edit3,
  manual_delete: Trash2,
  manual_restore: RotateCcw,
  add_extras: PlusCircle,
  total_edit: Edit3,
  csv_import: FileSpreadsheet,
  csv_import_undo: Undo2,
  ical_sync: RefreshCw,
  history_restore: RotateCcw,
  cancel: Trash2,
  duplicate: PlusCircle,
  assign_room: Edit3,
  parking_type: Edit3,
  gift_ticket: PlusCircle,
  receipt_upload: PlusCircle,
};
const EVENT_COLORS = {
  manual_create: "bg-emerald-100 text-emerald-700 border-emerald-300",
  manual_edit: "bg-blue-100 text-blue-700 border-blue-300",
  manual_delete: "bg-red-100 text-red-700 border-red-300",
  manual_restore: "bg-emerald-100 text-emerald-700 border-emerald-300",
  add_extras: "bg-amber-100 text-amber-700 border-amber-300",
  total_edit: "bg-blue-100 text-blue-700 border-blue-300",
  csv_import: "bg-indigo-100 text-indigo-700 border-indigo-300",
  csv_import_undo: "bg-orange-100 text-orange-700 border-orange-300",
  ical_sync: "bg-sky-100 text-sky-700 border-sky-300",
  history_restore: "bg-purple-100 text-purple-700 border-purple-300",
  cancel: "bg-red-100 text-red-700 border-red-300",
};

export default function BookingHistoryTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [confirmSnap, setConfirmSnap] = useState(null); // snapshot pending restore
  const [detailSnap, setDetailSnap] = useState(null); // snapshot to show details
  const [filter, setFilter] = useState(""); // by event_type

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?event_type=${encodeURIComponent(filter)}&limit=200` : "?limit=200";
      const [list, statsRes] = await Promise.all([
        api.get(`/admin/booking-history${q}`),
        api.get("/admin/booking-history/stats/overview"),
      ]);
      setItems(list.data?.items || []);
      setStats(statsRes.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento storico");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const askRestore = async (snap) => {
    // Load full detail (includes before state) to preview
    try {
      const { data } = await api.get(`/admin/booking-history/${snap.id}`);
      setConfirmSnap(data.snapshot);
    } catch (e) {
      toast.error("Errore caricamento dettaglio");
    }
  };

  const showDetail = async (snap) => {
    try {
      const { data } = await api.get(`/admin/booking-history/${snap.id}`);
      setDetailSnap(data.snapshot);
    } catch (e) {
      toast.error("Errore caricamento dettaglio");
    }
  };

  const confirmRestore = async () => {
    if (!confirmSnap) return;
    setRestoringId(confirmSnap.id);
    try {
      const { data } = await api.post(`/admin/booking-history/${confirmSnap.id}/restore`);
      toast.success(
        `Ripristinato · ${data.restored_bookings || 0} prenotazioni ripristinate` +
        (data.deleted_new_bookings ? ` · ${data.deleted_new_bookings} nuove cancellate` : "") +
        (data.restored_externals ? ` · ${data.restored_externals} iCal ripristinati` : ""),
        { duration: 8000 },
      );
      setConfirmSnap(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore ripristino");
    } finally {
      setRestoringId(null);
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      const today = new Date();
      const yest = new Date(); yest.setDate(yest.getDate() - 1);
      const isToday = d.toDateString() === today.toDateString();
      const isYest = d.toDateString() === yest.toDateString();
      const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
      if (isToday) return `Oggi ${time}`;
      if (isYest) return `Ieri ${time}`;
      return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="booking-history-tab">
      {/* Header */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#AC5D45]" />
            <h2 className="font-display text-xl text-[#1E3A5F]">Storico & Ripristino</h2>
            {stats?.total > 0 && (
              <span className="ml-1 bg-[#AC5D45] text-white text-xs font-bold rounded-full px-2 py-0.5">
                {stats.total}
              </span>
            )}
          </div>
          <Button onClick={load} variant="outline" size="sm" data-testid="history-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
          </Button>
        </div>
        <p className="text-sm text-[#635F59] leading-relaxed mt-2">
          Ogni modifica alle prenotazioni (manuale, import CSV, cancellazione, extras) viene salvata automaticamente.
          Puoi <b>ripristinare qualsiasi evento</b> degli ultimi 30 giorni con un click — riporta prenotazioni e blocchi iCal allo stato antecedente.
        </p>
        {stats && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-3 py-1">
              Ultime 24h: <b>{stats.last_24h}</b>
            </span>
            <span className="bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-3 py-1">
              Ultimi 7 giorni: <b>{stats.last_7d}</b>
            </span>
            <span className="bg-stone-100 text-stone-700 border border-stone-200 rounded-full px-3 py-1">
              Ritenzione: ultimi {stats.retention_days || 30} giorni
            </span>
          </div>
        )}
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs text-[#635F59]">Filtra:</span>
        {[
          { v: "", l: "Tutti" },
          { v: "manual_edit", l: "Modifiche" },
          { v: "manual_delete", l: "Cancellazioni" },
          { v: "add_extras", l: "Extra" },
          { v: "csv_import", l: "Import CSV" },
          { v: "history_restore", l: "Ripristini" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              filter === f.v
                ? "bg-[#1E3A5F] text-white border-[#1E3A5F]"
                : "bg-white text-[#635F59] border-stone-300 hover:border-[#AC5D45]"
            }`}
            data-testid={`history-filter-${f.v || "all"}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* List */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#AC5D45]" />
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-8 text-center" data-testid="history-empty">
          <History className="w-10 h-10 text-[#B8B0A4] mx-auto mb-3" />
          <p className="text-[#635F59]">Nessun evento registrato.</p>
          <p className="text-xs text-[#B8B0A4] mt-1">Modifica una prenotazione o importa un CSV per vedere il primo evento qui.</p>
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="history-list">
          {items.map((it) => {
            const Icon = EVENT_ICONS[it.event_type] || History;
            const colorClass = EVENT_COLORS[it.event_type] || "bg-stone-100 text-stone-700 border-stone-300";
            const alreadyRestored = !!it.restored_at;
            return (
              <div
                key={it.id}
                className={`bg-white border rounded-xl p-4 transition ${
                  alreadyRestored ? "border-stone-200 opacity-60" : "border-[#E8E2D9] hover:border-[#AC5D45]/50"
                }`}
                data-testid={`history-item-${it.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${colorClass}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="font-semibold text-[#1E3A5F] truncate">
                        {it.event_label || it.event_type}
                      </div>
                      <div className="text-xs text-[#635F59] flex items-center gap-1 shrink-0">
                        <Clock className="w-3 h-3" /> {fmtDateTime(it.created_at)}
                      </div>
                    </div>
                    <div className="text-sm text-[#635F59] mt-0.5 truncate">
                      {it.event_summary}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-[#635F59]">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" /> {it.actor}
                      </span>
                      {it.bookings_count > 0 && (
                        <>
                          <span className="text-stone-400">·</span>
                          <span>{it.bookings_count} prenotazioni</span>
                        </>
                      )}
                      {it.externals_count > 0 && (
                        <>
                          <span className="text-stone-400">·</span>
                          <span>{it.externals_count} iCal</span>
                        </>
                      )}
                      {alreadyRestored && (
                        <>
                          <span className="text-stone-400">·</span>
                          <span className="text-purple-700 font-semibold">
                            ✓ Ripristinato {fmtDateTime(it.restored_at)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => showDetail(it)}
                      data-testid={`history-detail-${it.id}`}
                    >
                      <Info className="w-4 h-4 mr-1" /> Dettagli
                    </Button>
                    {!alreadyRestored && (
                      <Button
                        size="sm"
                        className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
                        onClick={() => askRestore(it)}
                        disabled={restoringId === it.id}
                        data-testid={`history-restore-${it.id}`}
                      >
                        {restoringId === it.id
                          ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          : <Undo2 className="w-4 h-4 mr-1" />}
                        Ripristina
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm restore modal */}
      {confirmSnap && (
        <div className="fixed inset-0 z-[210] bg-black/60 flex items-center justify-center p-4" data-testid="history-confirm-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b bg-[#AC5D45]/5">
              <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
                <Undo2 className="w-5 h-5" /> Ripristina questo evento?
              </h3>
              <p className="text-xs text-[#635F59] mt-1">
                <b>{confirmSnap.event_label}</b> del <b>{fmtDateTime(confirmSnap.created_at)}</b>
              </p>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3 text-sm">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900">
                <b>Riepilogo:</b> {confirmSnap.event_summary}
              </div>
              <div className="text-xs text-[#635F59]">
                Verranno riportate allo stato PRIMA dell&apos;evento:
              </div>
              <ul className="text-xs text-[#1E3A5F] list-disc pl-5 space-y-0.5">
                {confirmSnap.affected_codes?.length > 0 && (
                  <li><b>{confirmSnap.affected_codes.length}</b> prenotazioni: {confirmSnap.affected_codes.slice(0, 6).join(", ")}{confirmSnap.affected_codes.length > 6 ? "…" : ""}</li>
                )}
                {confirmSnap.affected_external_ids?.length > 0 && (
                  <li><b>{confirmSnap.affected_external_ids.length}</b> blocchi iCal</li>
                )}
                {confirmSnap.is_creation && (
                  <li className="text-red-700">
                    Eventuali prenotazioni <b>create</b> da questo evento verranno cancellate
                  </li>
                )}
              </ul>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
                💡 <b>Rete di sicurezza</b>: verrà creato automaticamente un nuovo snapshot pre-ripristino, così potrai annullare anche questa operazione se cambi idea.
              </div>
            </div>
            <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmSnap(null)}
                className="text-xs text-stone-500 hover:text-stone-700 px-3 py-2"
                data-testid="history-confirm-cancel"
              >
                Annulla
              </button>
              <button
                onClick={confirmRestore}
                disabled={restoringId === confirmSnap.id}
                className="text-sm bg-[#AC5D45] hover:bg-[#8A4936] disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-2"
                data-testid="history-confirm-apply"
              >
                {restoringId === confirmSnap.id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Undo2 className="w-4 h-4" />}
                Sì, ripristina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailSnap && (
        <div className="fixed inset-0 z-[210] bg-black/60 flex items-center justify-center p-4" data-testid="history-detail-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
                <Info className="w-5 h-5" /> Dettaglio evento
              </h3>
              <p className="text-xs text-[#635F59] mt-1">
                {detailSnap.event_label} · {fmtDateTime(detailSnap.created_at)} · {detailSnap.actor}
              </p>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4 text-sm">
              <div>
                <div className="text-xs font-bold text-[#1E3A5F] uppercase mb-1">Riepilogo</div>
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">{detailSnap.event_summary}</div>
              </div>
              {detailSnap.before?.bookings?.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-[#1E3A5F] uppercase mb-1">
                    Stato precedente ({detailSnap.before.bookings.length} prenotazioni)
                  </div>
                  <div className="space-y-1">
                    {detailSnap.before.bookings.slice(0, 20).map((b, idx) => (
                      <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs">
                        <div className="flex flex-wrap gap-x-2 items-baseline">
                          <span className="font-mono text-blue-700">{b.code}</span>
                          <span className="text-stone-500">·</span>
                          <span className="font-semibold text-[#1E3A5F]">
                            {[b.guest?.first_name, b.guest?.last_name].filter(Boolean).join(" ") || "-"}
                          </span>
                          <span className="text-stone-500">·</span>
                          <span className="text-[#635F59]">{b.room_name || "-"}</span>
                          <span className="text-stone-500">·</span>
                          <span className="text-[#635F59]">{b.check_in} → {b.check_out}</span>
                          {b.total != null && (
                            <>
                              <span className="text-stone-500">·</span>
                              <span className="text-[#635F59]">€{Number(b.total).toFixed(2)}</span>
                            </>
                          )}
                          <span className="text-stone-500">·</span>
                          <span className="text-[#635F59]">stato: <b>{b.status}</b></span>
                        </div>
                      </div>
                    ))}
                    {detailSnap.before.bookings.length > 20 && (
                      <div className="text-xs text-stone-500 text-center py-2">
                        … e altre {detailSnap.before.bookings.length - 20} prenotazioni
                      </div>
                    )}
                  </div>
                </div>
              )}
              {detailSnap.before?.external_bookings?.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-[#1E3A5F] uppercase mb-1">
                    Blocchi iCal precedenti ({detailSnap.before.external_bookings.length})
                  </div>
                  <div className="text-xs text-[#635F59]">
                    {detailSnap.before.external_bookings.length} blocchi Booking/Airbnb inclusi nello snapshot.
                  </div>
                </div>
              )}
              {detailSnap.restored_at && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-800">
                  ✓ Questo evento è stato <b>ripristinato</b> il {fmtDateTime(detailSnap.restored_at)} da <b>{detailSnap.restored_by}</b>.
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t flex items-center justify-end">
              <button
                onClick={() => setDetailSnap(null)}
                className="text-sm text-stone-600 hover:text-stone-800 px-4 py-2"
                data-testid="history-detail-close"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
